import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { LogOut } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { platformService } from '@/services/api/platform.service';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const NAV = [
  { href: '/platform', label: 'Dashboard', exact: true },
  { href: '/platform/users', label: 'Impersonate' },
  { href: '/platform/invitation-codes', label: 'Invitation codes' },
];

interface PlatformLayoutProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Wider pages (tables with many columns) opt out of the default max width. */
  wide?: boolean;
}

/** Shell for the super-admin console: brand bar, section nav, page title, log out. */
export function PlatformLayout({ title, subtitle, actions, children, wide = false }: PlatformLayoutProps) {
  const [location, setLocation] = useLocation();

  const handleLogout = async () => {
    await platformService.logout();
    apiClient.clearAuth();
    setLocation('/platform/login');
  };

  const isActive = (item: (typeof NAV)[number]) =>
    item.exact ? location === item.href : location.startsWith(item.href);

  return (
    <div className="min-h-screen bg-surface-2">
      <header className="border-b border-line bg-surface">
        <div className={cn('mx-auto flex items-center justify-between gap-4 px-4 py-3', wide ? 'max-w-6xl' : 'max-w-4xl')}>
          <Link href="/platform" className="flex items-center gap-2">
            <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="h-7 w-auto" />
            <span className="rounded-full bg-ink px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
              Platform
            </span>
          </Link>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="mr-1.5 h-4 w-4" aria-hidden />
            Log out
          </Button>
        </div>
        <nav
          aria-label="Platform sections"
          className={cn('mx-auto flex gap-1 overflow-x-auto px-4', wide ? 'max-w-6xl' : 'max-w-4xl')}
        >
          {NAV.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  active ? 'border-brand-500 text-ink' : 'border-transparent text-ink-subtle hover:text-ink',
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className={cn('mx-auto px-4 py-8', wide ? 'max-w-6xl' : 'max-w-4xl')}>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-ink-subtle">{subtitle}</p>}
          </div>
          {actions}
        </div>

        {children}
      </main>
    </div>
  );
}
